// ── Tour Category System ─────────────────────────────────────────────────────

export const TOUR_CATEGORIES = [
  'sales_admin',
  'tour_preparation',
  'leader_handover',
  'leader_signoff',
  'post_tour',
];

export const CATEGORY_LABELS = {
  sales_admin:      'Sales & Admin',
  tour_preparation: 'Tour Preparation (Operations)',
  leader_handover:  'Operations to Tour Leader Handover',
  leader_signoff:   'Tour Leader Sign Off',
  post_tour:        'Post Tour',
  cancelled:        'Cancelled',
};

export const CATEGORY_STYLES = {
  sales_admin:      'bg-blue-light text-pb',
  tour_preparation: 'bg-amber-light text-[#92400E]',
  leader_handover:  'bg-[#CCFBF1] text-bt',
  leader_signoff:   'bg-green-light text-sg',
  post_tour:        'bg-[#E8EDF5] text-navy',
  cancelled:        'bg-[#FEE2E2] text-dr',
};

export const CATEGORY_DOT = {
  sales_admin:      'bg-pb',
  tour_preparation: 'bg-ba',
  leader_handover:  'bg-bt',
  leader_signoff:   'bg-sg',
  post_tour:        'bg-navy',
  cancelled:        'bg-dr',
};

/** Accent bg used for the expanded accordion header */
export const CATEGORY_ACCENT = {
  sales_admin:      'border-pb/30 bg-blue-light/60',
  tour_preparation: 'border-ba/30 bg-amber-light/60',
  leader_handover:  'border-bt/30 bg-[#CCFBF1]/60',
  leader_signoff:   'border-sg/30 bg-green-light/60',
  post_tour:        'border-navy/30 bg-[#E8EDF5]/60',
};

// Solid hex colors for each category (for charts, SVG elements, inline styles)
export const CATEGORY_COLORS = {
  sales_admin:      '#1A4A8A',
  tour_preparation: '#E8A020',
  leader_handover:  '#0F7B72',
  leader_signoff:   '#1A7A4A',
  post_tour:        '#0D2240',
  cancelled:        '#C0392B',
};

// Light fill hex colors (matching tailwind tokens)
export const CATEGORY_FILLS = {
  sales_admin:      '#EBF2FC',
  tour_preparation: '#FEF3C7',
  leader_handover:  '#CCFBF1',
  leader_signoff:   '#D1FAE5',
  post_tour:        '#E8EDF5',
  cancelled:        '#FEE2E2',
};

// Short display names for chart legends
export const CATEGORY_SHORT = {
  sales_admin:      'Sales',
  tour_preparation: 'Ops Prep',
  leader_handover:  'Handover',
  leader_signoff:   'Sign-off',
  post_tour:        'Post',
  cancelled:        'Cancelled',
};

export const CATEGORY_CHECK_COLOR = {
  sales_admin:      'text-pb',
  tour_preparation: 'text-[#92400E]',
  leader_handover:  'text-bt',
  leader_signoff:   'text-sg',
  post_tour:        'text-navy',
};

export const CATEGORY_STAGES = {
  sales_admin: [
    { key: 'initial_interest',  label: 'Initial interest' },
    { key: 'price_preparation', label: 'Price preparation' },
    { key: 'quote_sent',        label: 'Quote sent' },
    { key: 'date_confirmed',    label: 'Date confirmed' },
    { key: 'deposit_paid',      label: 'Deposit paid' },
    { key: 'tour_prep_start',   label: 'Tour preparation' },
  ],
  tour_preparation: [
    { key: 'quote_matching_cost_cal',  label: 'Quote matching cost cal' },
    { key: 'team_participant_form',    label: 'Send Team/Participant form' },
    { key: 'skeletal_built',           label: 'Skeletal itinerary built' },
    { key: 'supplier_requests_sent',   label: 'Send request to suppliers for confirmation' },
    { key: 'la_liga_confirmed',        label: 'La Liga match confirmed' },
    { key: 'fixtures_confirmed',       label: 'Fixtures confirmed' },
    { key: 'training_confirmed',       label: 'Training sessions confirmed' },
    { key: 'skeletal_admin_signoff',   label: 'Skeletal itinerary sign off by admin (internal)' },
    { key: 'skeletal_contact_signoff', label: 'Skeletal itinerary sign off by tour contact (external)' },
    { key: 'full_itinerary',           label: 'Full itinerary' },
    { key: 'chase_suppliers',          label: 'Manually chase suppliers if not confirmed' },
    { key: 'rooming_confirmed',        label: 'Rooming confirmed by tour contact (external)' },
    { key: 'final_call',               label: 'Final call with tour contact and tour leader' },
    { key: 'welcome_presentation',     label: 'Welcome presentation prepared' },
  ],
  leader_handover: [
    { key: 'assign_tour_leader', label: 'Assign a tour leader' },
    { key: 'generate_tick_list', label: 'Generate tour leader tick list' },
    { key: 'sign_tick_list',     label: 'Sign tick list' },
  ],
  leader_signoff: [
    { key: 'tick_list_complete', label: 'Tick list complete' },
    { key: 'photos_in_drive',    label: 'Photos in drive' },
    { key: 'accounts_signoff',   label: 'Accounts sign off' },
  ],
  post_tour: [
    { key: 'feedback_form',      label: 'Feedback form' },
    { key: 'complete_accounts',  label: 'Complete accounts sign off' },
    { key: 'social_media_posts', label: 'Social media posts' },
  ],
};

// ── Admin-editable checklists ────────────────────────────────────────────────
// CATEGORY_STAGES above is the default checklist. Admins can override it per
// portal in Admin Settings → Status Pipeline, saved to settings/statusPipeline:
//   { [portal]: { [categoryKey]: [{ key, label, removed? }] } }
// A category with no saved list uses the default. Removed items keep their key
// (and every tour's ticks) so restoring one brings its progress back.
export const PIPELINE_SETTINGS_DOC = ['settings', 'statusPipeline'];

// Items other features read directly by key, so they can be renamed or moved
// but never removed: the customer-facing status sync (quote_sent,
// deposit_paid), the dashboard's "new tours" count (initial_interest) and the
// post-tour auto-archive in functions/index.js.
export const LOCKED_STAGE_KEYS = new Set([
  'initial_interest', 'quote_sent', 'deposit_paid',
  'feedback_form', 'complete_accounts', 'social_media_posts',
]);

/** Every item for a category in a portal, including removed ones (for the editor). */
export function getAllCategoryStages(categoryKey, pipelineConfig, portal) {
  const custom = pipelineConfig?.[portal]?.[categoryKey];
  return Array.isArray(custom) ? custom : (CATEGORY_STAGES[categoryKey] || []);
}

/** The live checklist for a category in a portal. */
export function getCategoryStages(categoryKey, pipelineConfig, portal) {
  return getAllCategoryStages(categoryKey, pipelineConfig, portal).filter((s) => !s.removed);
}

/** Returns true when every sub-stage in the category is ticked */
export function isCategoryComplete(categoryKey, stageProgress = {}, stages = CATEGORY_STAGES[categoryKey] || []) {
  return stages.every((s) => !!stageProgress?.[s.key]);
}

/** Next category in the sequence, or null if at the end */
export function nextCategory(categoryKey) {
  const idx = TOUR_CATEGORIES.indexOf(categoryKey);
  return idx >= 0 && idx < TOUR_CATEGORIES.length - 1 ? TOUR_CATEGORIES[idx + 1] : null;
}

/** Previous category in the sequence, or null if at the start */
export function prevCategory(categoryKey) {
  const idx = TOUR_CATEGORIES.indexOf(categoryKey);
  return idx > 0 ? TOUR_CATEGORIES[idx - 1] : null;
}

/** Whether participant registration should be shown for this tour */
export function showParticipants(category, stageProgress = {}) {
  if (['tour_preparation', 'leader_handover', 'leader_signoff', 'post_tour'].includes(category)) return true;
  if (category === 'sales_admin' && stageProgress['deposit_paid']) return true;
  return false;
}

/**
 * Stage keys that, when ticked, also sync a legacy `status` value on the tour doc.
 * This keeps customer-facing views correct.
 */
export const STAGE_STATUS_SYNC = {
  quote_sent:    'quote_sent',
  deposit_paid:  'deposit_paid',
};

/**
 * Category advances that sync a legacy `status` value.
 */
export const CATEGORY_STATUS_SYNC = {
  tour_preparation: 'pre_tour',
  leader_handover:  'pre_tour',
  leader_signoff:   'tour_live',
  post_tour:        'completed',
  cancelled:        'cancelled',
};

// ── Legacy status system (used by customer-facing views) ─────────────────────

export const STATUS_ORDER = [
  'submitted', 'reviewing', 'quote_sent', 'quote_approved',
  'deposit_invoice_sent', 'deposit_paid', 'pre_tour', 'tour_live', 'completed',
];

export const ALL_STATUSES = [...STATUS_ORDER, 'cancelled'];

export const STATUS_LABELS = {
  submitted:            'Submitted',
  reviewing:            'Under Review',
  quote_sent:           'Quote Sent',
  quote_approved:       'Quote Approved',
  deposit_invoice_sent: 'Deposit Invoice Sent',
  deposit_paid:         'Deposit Paid',
  pre_tour:             'Pre-Tour Build',
  tour_live:            'Tour Live',
  completed:            'Tour Completed',
  cancelled:            'Cancelled',
  quoted:               'Quote Sent',
  confirmed:            'Confirmed',
};

export const STATUS_STYLES = {
  submitted:            'bg-[#EFF6FF] text-[#1D4ED8]',
  reviewing:            'bg-[#FFF7ED] text-[#92400E]',
  quote_sent:           'bg-[#F5F3FF] text-[#5B21B6]',
  quote_approved:       'bg-[#F5F3FF] text-[#5B21B6]',
  deposit_invoice_sent: 'bg-[#FFF7ED] text-[#92400E]',
  deposit_paid:         'bg-[#ECFDF5] text-[#065F46]',
  pre_tour:             'bg-[#ECFDF5] text-[#065F46]',
  tour_live:            'bg-[#ECFDF5] text-[#065F46]',
  completed:            'bg-[#ECFDF5] text-[#065F46]',
  cancelled:            'bg-[#FEE2E2] text-[#991B1B]',
  quoted:               'bg-[#F5F3FF] text-[#5B21B6]',
  confirmed:            'bg-[#ECFDF5] text-[#065F46]',
};

export const STATUS_DOT = {
  submitted:            'bg-[#3B82F6]',
  reviewing:            'bg-[#F59E0B]',
  quote_sent:           'bg-[#8B5CF6]',
  quote_approved:       'bg-[#8B5CF6]',
  deposit_invoice_sent: 'bg-[#F59E0B]',
  deposit_paid:         'bg-[#10B981]',
  pre_tour:             'bg-[#10B981]',
  tour_live:            'bg-[#10B981]',
  completed:            'bg-[#10B981]',
  cancelled:            'bg-[#C0392B]',
  quoted:               'bg-[#8B5CF6]',
  confirmed:            'bg-[#10B981]',
};

export const VALID_TRANSITIONS = {
  submitted:            ['reviewing', 'cancelled'],
  reviewing:            ['quote_sent', 'cancelled'],
  quote_sent:           ['quote_approved', 'reviewing', 'cancelled'],
  quote_approved:       ['deposit_invoice_sent', 'cancelled'],
  deposit_invoice_sent: ['deposit_paid', 'cancelled'],
  deposit_paid:         ['pre_tour', 'cancelled'],
  pre_tour:             ['tour_live', 'cancelled'],
  tour_live:            ['completed', 'cancelled'],
  completed:            [],
  cancelled:            [],
  quoted:               ['quote_approved', 'reviewing', 'cancelled'],
  confirmed:            [],
};

export const QUOTE_SENT_STATUSES = [
  'quote_sent', 'quote_approved', 'deposit_invoice_sent',
  'deposit_paid', 'pre_tour', 'tour_live', 'completed', 'quoted',
];

export const PARTICIPANT_STATUSES = ['deposit_paid', 'pre_tour', 'tour_live', 'completed', 'confirmed'];
